import { CommonModule } from '@angular/common';
import { Component, inject, signal, ViewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, MaybeAsync, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { ConversationApiService } from '@vendasta/conversation';
import { Widget } from '../webchat-form/widget';
import { GalaxyPageModule } from '@vendasta/galaxy/page';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { catchError, firstValueFrom, throwError } from 'rxjs';
import { InboxService } from '../../../../../core/src';
import { ACCOUNT_GROUP_ID_TOKEN, PARTNER_ID_TOKEN } from '../../../../../core/src/lib/tokens';
import { StickyFooterComponent } from '../../../components/sticky-footer/sticky-footer.component';
import { getBackUrl } from '../constants';
import { WebchatFormComponent } from '../webchat-form/webchat-form.component';
import { AiKnowledgeService } from '@galaxy/ai-knowledge';
import { KnowledgeSource } from '@vendasta/embeddings';

@Component({
  selector: 'inbox-new-webchat',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    GalaxyPageModule,
    TranslateModule,
    MatIconModule,
    WebchatFormComponent,
    StickyFooterComponent,
  ],
  templateUrl: './new-webchat.component.html',
  styleUrls: ['./new-webchat.component.scss'],
})
export class NewWebchatComponent {
  @ViewChild('webchatFormComponent') webchatFormComponent: WebchatFormComponent;
  private readonly conversationApi = inject(ConversationApiService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly partnerId = toSignal(inject(PARTNER_ID_TOKEN));
  private readonly accountGroupId = toSignal(inject(ACCOUNT_GROUP_ID_TOKEN));
  private readonly inboxService = inject(InboxService);
  private readonly knowledgeService = inject(AiKnowledgeService);

  private widgetFormData: Widget;
  private knowledgeSources?: KnowledgeSource[];

  readonly isLoading = signal(false);
  readonly backUrl = getBackUrl(this.inboxService.isBusinessApp, 2);

  updateFormData(widget: Widget): void {
    this.widgetFormData = widget;
  }

  updateKnowledgeSources(sources: KnowledgeSource[]): void {
    this.knowledgeSources = sources;
  }

  async createWebchat(): Promise<void> {
    this.isLoading.set(true);

    // We may need to revisit how we get namespaces for the settings page.
    // This works for now
    const namespace = this.accountGroupId() || this.partnerId();

    try {
      const resp = await firstValueFrom(
        this.conversationApi
          .createWidget({
            ...this.widgetFormData,
            namespace: namespace,
          })
          .pipe(catchError((err) => throwError(() => err))),
      );

      const widgetId = resp.widget.widgetId;
      const configurationUrl = await this.inboxService.buildAiKnowledgeAppConfigurationUrl(widgetId);
      await this.knowledgeService
        .upsertKnowledgeForApp(
          this.inboxService.buildAppId(widgetId),
          'Web Chat: ' + resp.widget.name,
          configurationUrl,
          this.knowledgeSources,
        )
        .catch(
          // ignore error here for now because it could orphan the widget otherwise
          // TODO: handle this better in the next iteration of the UI for AI Knowledge (a different step?)
          () => console.warn('Failed to save Automated Assistant settings'),
        );

      this.webchatFormComponent.markAsSaved();
      this.snackbarService.openSuccessSnack('INBOX.WEBCHAT.SETTINGS.WEBCHAT_CREATED');
      this.router.navigate(['..', resp.widget.widgetId], { relativeTo: this.route });
    } catch (e) {
      this.snackbarService.openErrorSnack('INBOX.WEBCHAT.SETTINGS.WEBCHAT_CREATE_ERROR');
    } finally {
      this.isLoading.set(false);
    }
  }

  canDeactivate(): MaybeAsync<boolean> {
    return this.webchatFormComponent.canDeactivate();
  }
}
